/**
 * AvatarPlayer - Unified entry point for RTC avatar streaming.
 *
 * This class provides a unified API that shields applications from
 * differences between different RTC providers (LiveKit, Agora, etc.)
 * and integrates seamlessly with @spatius/avatarkit for rendering.
 *
 * @packageDocumentation
 */

import type { RTCProvider } from './RTCProvider';
import type {
  RTCConnectionConfig,
  RTCPrepareConnectionConfig,
  StreamStats,
} from '../types';
import { AnimationHandler } from './AnimationHandler';
import type { AnimationTrackCallbacks } from './types';
import {
  configureLogger,
  logger,
  type LogLevel,
  initializeTelemetry,
  trackEvent,
  recordMetric,
  flushTelemetry,
} from '../utils';

// Import AvatarView type from avatarkit
import type { AvatarView } from '@spatius/avatarkit';
// Runtime import (not type-only): the constructor reads the live configuration to
// enforce that the host initialized avatarkit in RTC mode. See assertRtcMode.
import { AvatarSDK, DrivingServiceMode } from '@spatius/avatarkit';

/**
 * Fail fast unless avatarkit was initialized with `drivingServiceMode: 'rtc'`.
 *
 * The mode is what every telemetry record carries as `dsm`. RTC-driven sessions
 * are indistinguishable from ordinary `backend` ones without it, so a wrong mode
 * here silently poisons the dashboards for the entire session — and silently, in
 * a way nobody notices until someone tries to read RTC numbers weeks later. That
 * is worth refusing to start over: the fix is one argument at `initialize`, and
 * it can only be made before any data is produced.
 *
 * Throws rather than warns deliberately, at the author's request: with a warning
 * the misconfiguration ships.
 */
function assertRtcMode(): void {
  // A host on an avatarkit older than the declared peer range predates the `rtc`
  // enum value, so it *cannot* satisfy this contract by passing an argument —
  // say so instead of asking them to set a value their version does not accept.
  // Checked at runtime rather than pinned to a version string: the peer range is
  // what governs compatibility, and a hardcoded "requires >= x.y.z" here would go
  // stale the moment that range moves.
  if (DrivingServiceMode.rtc === undefined) {
    throw new Error(
      '@spatius/avatarkit-rtc requires a version of @spatius/avatarkit that supports ' +
        'DrivingServiceMode.rtc. Please upgrade @spatius/avatarkit.',
    );
  }

  const configuration = AvatarSDK.configuration;
  if (configuration === null) {
    throw new Error(
      'AvatarSDK.initialize() must be called before constructing AvatarPlayer, ' +
        "with drivingServiceMode: DrivingServiceMode.rtc.",
    );
  }

  const mode = configuration.drivingServiceMode;
  if (mode !== DrivingServiceMode.rtc) {
    throw new Error(
      `AvatarPlayer requires avatarkit to be initialized with ` +
        `drivingServiceMode: DrivingServiceMode.rtc, but it is "${mode ?? 'direct'}". ` +
        `All telemetry from this session would be reported under that mode, making ` +
        `RTC traffic indistinguishable from it. Pass drivingServiceMode: ` +
        `DrivingServiceMode.rtc to AvatarSDK.initialize().`,
    );
  }
}

/**
 * A provider that supports route B (external client) — i.e. borrowing a
 * host-owned RTC client instead of creating one. Duck-typed to avoid coupling
 * AvatarPlayer to a concrete provider class.
 * @internal
 */
interface ExternalClientProvider {
  attachExternalClient(client: unknown): Promise<void>;
  detachExternalClient(): void;
}

/** @internal */
function supportsExternalClient(
  provider: RTCProvider,
): provider is RTCProvider & ExternalClientProvider {
  return (
    typeof (provider as Partial<ExternalClientProvider>)
      .attachExternalClient === 'function' &&
    typeof (provider as Partial<ExternalClientProvider>)
      .detachExternalClient === 'function'
  );
}

/**
 * Options for configuring AvatarPlayer.
 */
export interface AvatarPlayerOptions {
  /**
   * Log level for SDK output.
   * - 'info': Show all logs (debug mode)
   * - 'warning': Show warnings and errors (default)
   * - 'error': Show only errors
   * - 'none': Disable all logs
   */
  logLevel?: LogLevel;

  /**
   * Enable jitter buffer for smoother animation playback.
   * When enabled, frames are buffered and rendered in sequence order at a steady 25fps,
   * absorbing network jitter and out-of-order delivery.
   * Default: true
   */
  enableJitterBuffer?: boolean;

  /**
   * Maximum delay (in ms) a frame can sit in the jitter buffer before being rendered.
   * Only used when enableJitterBuffer is true.
   * Default: 80 (2 frames at 25fps)
   */
  maxBufferDelayMs?: number;
}

interface StreamStatsCounters {
  framesLost: number;
  framesRecovered: number;
  framesDropped: number;
  framesOutOfOrder: number;
  framesDuplicate: number;
}

/**
 * Unified Avatar Player.
 *
 * Main entry point for applications to interact with RTC avatar streaming.
 * Handles connection management, audio publishing, animation rendering,
 * and coordinates with avatarkit for display.
 *
 * @example
 * ```typescript
 * import { AvatarPlayer, LiveKitProvider } from '@spatius/avatarkit-rtc';
 * import { AvatarView, Avatar } from '@spatius/avatarkit';
 *
 * // Create avatar and view (from avatarkit)
 * const avatar = await Avatar.create(characterId);
 * const avatarView = new AvatarView(avatar, container);
 *
 * // Create player
 * const provider = new LiveKitProvider();
 * const player = new AvatarPlayer(provider, avatarView);
 *
 * // Connect
 * await player.connect({ url: 'wss://...', token: '...' });
 *
 * // Option 1: Use microphone (most common)
 * await player.startPublishing();
 * await player.stopPublishing();
 *
 * // Option 2: Use custom audio source
 * const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
 * await player.publishAudio(stream.getAudioTracks()[0]);
 * await player.unpublishAudio();
 * stream.getTracks().forEach(t => t.stop());
 *
 * // Disconnect
 * await player.disconnect();
 * ```
 */
export class AvatarPlayer {
  /** @internal */
  private static readonly DISCONNECT_TRANSITION_FRAMES = 12;
  /** @internal */
  private static readonly DISCONNECT_TRANSITION_FRAME_INTERVAL_MS = 40;

  /** @internal */
  private provider: RTCProvider;

  /** @internal */
  private avatarView: AvatarView;

  /** @internal */
  private animationHandler: AnimationHandler;

  /** @internal */
  private _isConnected = false;

  /** @internal */
  private localAudioTrack: MediaStreamTrack | null = null;

  /** @internal */
  private microphoneStream: MediaStream | null = null;

  /** @internal */
  private eventHandlers: Map<string, Set<(...args: unknown[]) => void>> =
    new Map();

  /** @internal */
  private lastConnectionConfig: RTCConnectionConfig | null = null;

  /** @internal */
  private isReconnecting = false;

  /** @internal */
  private hasActiveAnimationSession = false;

  /** @internal */
  private isTrackingPrimed = false;

  /** @internal */
  private connectStartTime = 0;

  /** @internal */
  private sessionStartTime = 0;

  /** @internal */
  private stallCount = 0;

  /** @internal */
  private reconnectCount = 0;

  /** @internal */
  private conversationCount = 0;

  /** @internal */
  private providerName = '';

  /** @internal */
  private streamStatsAccumulator = {
    samples: 0,
    fpsSum: 0,
    fpsMax: 0,
    framesLost: 0,
    framesRecovered: 0,
    framesDropped: 0,
    framesOutOfOrder: 0,
    framesDuplicate: 0,
  };

  /** @internal */
  private previousStreamStatsCounters: StreamStatsCounters | null = null;

  /**
   * Last transport counter read, for differencing. Null before the first
   * sample of a connection and after it ends — the first read is a baseline,
   * not a delta.
   * @internal
   */
  private previousTransportCounters: {
    packetsLost?: number;
    packetsReceived?: number;
  } | null = null;

  /**
   * True while a reconnect owns the teardown/setup cycle.
   *
   * A reconnect internally calls disconnect() then connect(), and without this
   * flag those two would each behave as if the session had ended and a brand
   * new one begun: the summary would be emitted mid-session and every session
   * counter — including reconnectCount itself — reset to zero, which is why
   * reconnect_count was permanently 0 in the reported data.
   * @internal
   */
  private isSessionContinuation = false;

  /**
   * Guards against emitting rtc_session_summary twice for one session, since
   * teardown can arrive from disconnect(), detach(), or a server-initiated
   * drop.
   * @internal
   */
  private hasReportedSessionSummary = false;

  /**
   * True while the host's own disconnect()/detach() is tearing the session
   * down.
   *
   * Providers emit 'disconnected' from inside their disconnect(), i.e. before
   * this class has finished its own teardown, so the event alone cannot tell a
   * host-initiated close apart from a drop the host never asked for. This flag
   * is that discriminator.
   * @internal
   */
  private isTearingDown = false;

  /**
   * Create a new AvatarPlayer instance.
   * @param provider - RTC provider implementation (e.g., LiveKitProvider)
   * @param avatarView - AvatarView instance from @spatius/avatarkit
   * @param options - Optional configuration for transitions
   */
  constructor(
    provider: RTCProvider,
    avatarView: AvatarView,
    options?: AvatarPlayerOptions,
  ) {
    // Before anything else: a wrong driving mode mislabels every record this
    // session emits, and it can only be corrected at AvatarSDK.initialize().
    assertRtcMode();

    this.provider = provider;
    this.avatarView = avatarView;
    this.providerName = provider.name;

    // Configure logging if specified
    if (options?.logLevel !== undefined) {
      configureLogger({ level: options.logLevel });
    }

    // Initialize telemetry (piggybacks on avatarkit's PostHog instance)
    initializeTelemetry({ sdkVersion: __RTC_SDK_VERSION__ });
    logger.info('AvatarPlayer', `SDK version: ${__RTC_SDK_VERSION__}`);

    // Create internal renderer adapter
    const renderer = this.createRendererAdapter();

    this.animationHandler = new AnimationHandler(renderer, {
      enableJitterBuffer: options?.enableJitterBuffer,
      maxBufferDelayMs: options?.maxBufferDelayMs,
      providerName: this.providerName,
      onStreamStalled: () => {
        // AnimationHandler already switches to idle state
        // Just notify external listeners, let them decide what to do (e.g., call reconnect())
        this.hasActiveAnimationSession = false;
        this.isTrackingPrimed = false;
        this.stallCount++;
        trackEvent('rtc_stream_stalled', 'warning', {
          provider: this.providerName,
          conversation_id: this.animationHandler.getConversationId(),
          session_elapsed: this.sessionStartTime ? Date.now() - this.sessionStartTime : 0,
        });
        this.emit('stalled');
      },
    });

    // Setup provider event handlers
    this.setupProviderEvents();
  }

  /**
   * Check if currently connected to RTC server.
   */
  get isConnected(): boolean {
    return this._isConnected;
  }

  /**
   * Cumulative jitter-buffer / playback statistics for the current
   * AvatarPlayer lifetime. Mirrors the iOS surface — tests read this to
   * verify the state machine processed packets correctly (ordering, drops,
   * skips, stalls). Also emitted as `rtc_session_summary` on disconnect.
   */
  get sessionSummary(): ReturnType<AnimationHandler['getSessionSummary']> {
    return this.animationHandler.getSessionSummary();
  }

  /**
   * Connect to RTC server.
   * @param config - Connection configuration (URL, token, room name, etc.)
   * @throws Error if already connected or connection fails
   */
  async connect(config: RTCConnectionConfig): Promise<void> {
    if (this._isConnected) {
      throw new Error('Already connected. Please disconnect first.');
    }

    // Save config for potential reconnection
    this.lastConnectionConfig = config;
    this.connectStartTime = performance.now();

    trackEvent('rtc_connect_start', 'info', {
      provider: this.providerName,
    });

    try {
      // Setup animation callbacks before connecting
      await this.setupAnimationCallbacks();
      await this.provider.connect(config);
      this._isConnected = true;
      this.beginSession();

      trackEvent('rtc_connect_success', 'info', {
        provider: this.providerName,
        duration: Math.round(performance.now() - this.connectStartTime),
      });
    } catch (error) {
      trackEvent('rtc_connect_failed', 'error', {
        provider: this.providerName,
        description: error instanceof Error ? error.message : String(error),
        duration: Math.round(performance.now() - this.connectStartTime),
      });
      throw error;
    }
  }

  /**
   * Pre-warm a future RTC connection when the provider supports it.
   * This is best-effort and does not change the connected state.
   */
  async prepareConnection(config: RTCPrepareConnectionConfig): Promise<void> {
    await this.provider.prepareConnection?.(config);
  }

  /**
   * Disconnect from RTC server.
   * Stops all tracks and cleans up resources.
   */
  async disconnect(): Promise<void> {
    if (!this._isConnected) {
      return;
    }

    // Stop microphone if active
    if (this.microphoneStream) {
      await this.stopPublishing();
    } else if (this.localAudioTrack) {
      // Stop custom audio track
      await this.unpublishAudio();
    }

    this.isTearingDown = true;
    try {
      await this.provider.disconnect();
      this.animationHandler.dispose();
      await this.playTransitionToIdle();
      this._isConnected = false;
    } finally {
      this.isTearingDown = false;
    }

    this.endSession('client_disconnect');
  }

  /**
   * Start (or resume) session-scoped telemetry bookkeeping.
   *
   * During a reconnect this only re-arms the summary guard: the counters belong
   * to the logical session, which spans the reconnect, so resetting them would
   * discard exactly the history that makes a reconnect worth reporting.
   * @internal
   */
  private beginSession(): void {
    this.hasReportedSessionSummary = false;

    if (this.isSessionContinuation) {
      // Provider counters restart from zero on a fresh transport, so the delta
      // baseline must be dropped even though the accumulated totals are kept.
      this.previousStreamStatsCounters = null;
      // The baseline was dropped when the old transport went away, and the new
      // one starts its counters from zero; take a fresh opening reading so the
      // rounds that follow the reconnect still have something to difference
      // against. Skipping this left every reconnected session with no transport
      // stats from the reconnect onward.
      this.startTransportStatsSampling();
      return;
    }

    this.sessionStartTime = Date.now();
    this.stallCount = 0;
    this.reconnectCount = 0;
    this.conversationCount = 0;
    this.resetStreamStatsAccumulator();
    this.startTransportStatsSampling();
  }

  /**
   * Emit the end-of-session records exactly once per session.
   *
   * Suppressed while a reconnect is in flight — the session continues across
   * it, so a summary emitted there would describe a fragment and reset the
   * counters that the real summary needs.
   *
   * @param reason - what ended the session, so a server-initiated drop is
   *   distinguishable from a host calling disconnect()/detach()
   * @internal
   */
  private endSession(
    reason:
      | 'client_disconnect'
      | 'client_detach'
      | 'server_disconnect'
      | 'reconnect_failed',
  ): void {
    if (this.isSessionContinuation) return;
    if (this.hasReportedSessionSummary) return;
    this.hasReportedSessionSummary = true;
    this.stopTransportStatsSampling();

    const sessionDuration = this.sessionStartTime
      ? Date.now() - this.sessionStartTime
      : 0;

    // Scope: what happened to the *session*. Everything that happens inside a
    // playback round — frame counts, fps, jitter-buffer behaviour — is already
    // reported per round by rtc_playback_stats and is deliberately not
    // duplicated here. Aggregating those in the backend by conversation_id is
    // both more accurate and more flexible than re-summing them on the client,
    // which additionally loses the tail whenever a session is disposed
    // mid-round. Session-level avg_fps was the worst of them: averaging across
    // long idle stretches produced a number describing neither playback nor
    // idle, and reported 0 both when nothing played and when playback froze.
    trackEvent('rtc_session_summary', 'info', {
      provider: this.providerName,
      end_reason: reason,
      total_duration_ms: sessionDuration,
      conversation_count: this.conversationCount,
      reconnect_count: this.reconnectCount,
      // Times the stream went silent long enough to fall back to idle. Distinct
      // from the per-round stall_count, which counts drain cycles that rendered
      // nothing.
      stream_stalled_count: this.stallCount,
      // Transport-level packet counters. Kept because this is the only place
      // network loss/reordering is visible at all — rtc_playback_stats sees
      // only what survived to the renderer.
      //
      // These count *packets*, not frames: the sender emits a packet every
      // 20ms while a new animation frame arrives only every 40ms, so the rate
      // runs at roughly twice the frame rate. They were previously named
      // stream_avg_fps / stream_max_fps, which read as a frame rate and made a
      // healthy session look like it was rendering at ~50fps.
      stream_packets_per_sec_avg: this.getStreamAveragePacketRate(),
      stream_packets_per_sec_max: this.streamStatsAccumulator.fpsMax,
      stream_packets_lost: this.streamStatsAccumulator.framesLost,
      stream_packets_dropped: this.streamStatsAccumulator.framesDropped,
      stream_packets_out_of_order: this.streamStatsAccumulator.framesOutOfOrder,
      stream_packets_duplicate: this.streamStatsAccumulator.framesDuplicate,
    });

    trackEvent('rtc_disconnected', 'info', {
      provider: this.providerName,
      end_reason: reason,
      session_duration: sessionDuration,
    });

    flushTelemetry();
  }

  /**
   * Attach to a host-owned RTC client (route B / external client).
   *
   * Unlike {@link connect}, the SDK does not create, join, or leave the RTC
   * client — the host owns the full RTC lifecycle (client initialization,
   * connection, and disconnection). The SDK only attaches the provider-specific
   * animation extraction path and renders the resulting avatar frames.
   *
   * Call ordering (host side):
   * ```typescript
   * const room = new Room({ singlePeerConnection: false });
   * await player.attach(room);     // MUST be before room.connect(...)
   * await room.connect(url, token);
   * // ... avatar streams in ...
   * await player.detach();         // client is handed back untouched
   * ```
   *
   * The provider must support external clients (currently LiveKitProvider and
   * AgoraProvider). Do not mix with {@link connect} — the managed and
   * external-client paths are exclusive. Provider-specific construction and
   * call-order requirements still apply.
   *
   * @param client - The host-owned RTC client/room to borrow.
   * @throws Error if already connected, or the provider doesn't support it.
   */
  async attach(client: unknown): Promise<void> {
    if (this._isConnected) {
      throw new Error('Already connected. Please disconnect first.');
    }
    if (!supportsExternalClient(this.provider)) {
      throw new Error(
        'attach() requires a provider that supports external clients.',
      );
    }

    trackEvent('rtc_attach_start', 'info', { provider: this.providerName });
    try {
      // Wire up the animation/render callbacks exactly as connect() does —
      // without this the SEI payloads would be parsed but never rendered.
      await this.setupAnimationCallbacks();
      await this.provider.attachExternalClient(client);
      this._isConnected = true;
      this.beginSession();
      trackEvent('rtc_attach_success', 'info', { provider: this.providerName });
    } catch (error) {
      trackEvent('rtc_attach_failed', 'error', {
        provider: this.providerName,
        description: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /**
   * Detach from a host-owned client attached via {@link attach}. Removes the
   * SDK's animation listeners and returns the avatar to idle, but never
   * disconnects the host's client.
   */
  async detach(): Promise<void> {
    if (!this._isConnected || !supportsExternalClient(this.provider)) {
      return;
    }
    this.isTearingDown = true;
    try {
      this.provider.detachExternalClient();
      this.animationHandler.dispose();
      await this.playTransitionToIdle();
      this._isConnected = false;
    } finally {
      this.isTearingDown = false;
    }
    trackEvent('rtc_detached', 'info', { provider: this.providerName });

    // detach() ends the session just as surely as disconnect() does; the host
    // merely keeps its own client. Without this the entire route-B population
    // reported no session summary at all.
    this.endSession('client_detach');
  }

  /**
   * Play a soft transition from the current pose back to idle, then hand the
   * renderer back to AvatarKit. Mirrors AnimationHandler's server-driven
   * end-transition path so tearing a session down feels the same as a normal
   * conversation end, instead of snapping straight to the idle start frame.
   * @internal
   */
  private async playTransitionToIdle(): Promise<void> {
    const transitionFrames = await this.avatarView.generateTransitionToIdle(
      AvatarPlayer.DISCONNECT_TRANSITION_FRAMES,
    );
    // Reuse the same renderer adapter the AnimationHandler uses — it already
    // casts away the `@internal` renderFlame visibility, and matches how
    // server-driven end transitions feed frames back to AvatarKit.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const view = this.avatarView as any;
    for (const frame of transitionFrames) {
      await view.renderFlame(frame);
      await new Promise((resolve) =>
        setTimeout(
          resolve,
          AvatarPlayer.DISCONNECT_TRANSITION_FRAME_INTERVAL_MS,
        ),
      );
    }
    this.avatarView.startIdle();
  }

  /**
   * Publish an audio track to the RTC server.
   *
   * The audio source is controlled externally - you can pass any audio track:
   * - Microphone: `getUserMedia({ audio: true })`
   * - Browser audio: `audioElement.captureStream()`
   * - Web Audio API: `audioContext.createMediaStreamDestination().stream`
   * - Screen share audio: `getDisplayMedia({ audio: true })`
   *
   * @param track - MediaStreamTrack to publish (must be an audio track)
   * @throws Error if not connected or track is invalid
   *
   * @example
   * ```typescript
   * // Microphone
   * const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
   * await player.publishAudio(stream.getAudioTracks()[0]);
   *
   * // Browser audio element
   * const audioEl = document.querySelector('audio');
   * const stream = audioEl.captureStream();
   * await player.publishAudio(stream.getAudioTracks()[0]);
   * ```
   */
  async publishAudio(track: MediaStreamTrack): Promise<void> {
    if (!this._isConnected) {
      throw new Error('Not connected. Please call connect() first.');
    }

    if (track.kind !== 'audio') {
      throw new Error('Invalid track: expected audio track');
    }

    // Unpublish existing track if any
    if (this.localAudioTrack) {
      await this.unpublishAudio();
    }

    this.localAudioTrack = track;
    try {
      await this.provider.publishAudioTrack(track);
    } catch (error) {
      this.localAudioTrack = null;
      trackEvent('rtc_audio_publish_failed', 'error', {
        provider: this.providerName,
        description: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /**
   * Stop publishing audio.
   * Note: This does NOT stop the track - the caller is responsible for managing the track lifecycle.
   */
  async unpublishAudio(): Promise<void> {
    if (!this.localAudioTrack) {
      return;
    }

    await this.provider.unpublishAudioTrack();
    this.localAudioTrack = null;
  }

  /**
   * Start publishing microphone audio.
   *
   * This requests microphone permission and publishes it to the RTC session.
   * For custom audio sources (e.g., audio files), use `publishAudio(track)` instead.
   *
   * @throws Error if not connected, permission denied, or no microphone found
   *
   * @example
   * ```typescript
   * await player.startPublishing();
   * // ... later
   * await player.stopPublishing();
   * ```
   */
  async startPublishing(): Promise<void> {
    if (!this._isConnected) {
      throw new Error('Not connected. Please call connect() first.');
    }

    if (this.microphoneStream) {
      return; // Already using microphone
    }

    // Request microphone permission
    this.microphoneStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    const track = this.microphoneStream.getAudioTracks()[0];

    await this.publishAudio(track);
  }

  /**
   * Stop publishing microphone audio and release the microphone.
   *
   * This stops the microphone track and releases the device.
   * If you used `publishAudio()` with a custom track, use `unpublishAudio()` instead.
   */
  async stopPublishing(): Promise<void> {
    if (!this.microphoneStream) {
      return;
    }

    await this.unpublishAudio();

    // Stop and release microphone
    this.microphoneStream.getTracks().forEach((track) => track.stop());
    this.microphoneStream = null;
  }

  /**
   * Get current connection state.
   * @returns Connection state string (e.g., 'connected', 'disconnected')
   */
  getConnectionState(): string {
    return this.provider.getConnectionState();
  }

  /**
   * Get the native RTC client object.
   *
   * Returns the underlying RTC client for advanced use cases:
   * - LiveKit: Returns the Room instance
   * - Agora: Returns the IAgoraRTCClient instance
   *
   * @returns The native RTC client object, or null if not connected
   *
   * @example
   * ```typescript
   * // Access LiveKit Room
   * const room = player.getNativeClient() as Room;
   * console.log('Participants:', room?.remoteParticipants.size);
   *
   * // Access Agora Client
   * const client = player.getNativeClient() as IAgoraRTCClient;
   * console.log('State:', client?.connectionState);
   * ```
   */
  getNativeClient(): unknown {
    return this.provider.getNativeClient();
  }

  /**
   * Add event listener.
   * @param event - Event name ('connected', 'disconnected', 'error', 'stalled', etc.)
   * @param handler - Event handler function
   */
  on(event: string, handler: (...args: unknown[]) => void): void {
    if (event === 'stalled') {
      // Handle stalled event locally
      if (!this.eventHandlers.has(event)) {
        this.eventHandlers.set(event, new Set());
      }
      this.eventHandlers.get(event)!.add(handler);
    } else {
      // Forward other events to provider
      this.provider.on(event, handler);
    }
  }

  /**
   * Remove event listener.
   * @param event - Event name
   * @param handler - Event handler function (must be same reference as added)
   */
  off(event: string, handler: (...args: unknown[]) => void): void {
    if (event === 'stalled') {
      this.eventHandlers.get(event)?.delete(handler);
    } else {
      this.provider.off(event, handler);
    }
  }

  /**
   * Emit an event to local listeners.
   * @internal
   */
  private emit(event: string, ...args: unknown[]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(...args);
        } catch (e) {
          logger.error('AvatarPlayer', `Error in ${event} handler:`, e);
        }
      });
    }
  }

  /**
   * Reconnect to RTC server using the last connection configuration.
   * Useful for recovering from connection issues or stream stalls.
   *
   * @example
   * ```typescript
   * player.on('stalled', async () => {
   *   console.log('Stream stalled, attempting reconnection...');
   *   await player.reconnect();
   * });
   * ```
   *
   * @throws Error if no previous connection config exists or reconnection fails
   */
  async reconnect(): Promise<void> {
    if (this.isReconnecting) {
      logger.warn('AvatarPlayer', 'Already attempting reconnection, skipping');
      return;
    }

    if (!this.lastConnectionConfig) {
      throw new Error(
        'Cannot reconnect: no previous connection. Call connect() first.',
      );
    }

    this.isReconnecting = true;
    this.reconnectCount++;
    logger.info('AvatarPlayer', 'Attempting reconnection...');

    const reconnectStart = performance.now();
    trackEvent('rtc_reconnect_start', 'info', {
      provider: this.providerName,
      reconnect_count: this.reconnectCount,
    });

    // The session survives the reconnect, so the nested disconnect()/connect()
    // must not emit a summary or clear the counters.
    this.isSessionContinuation = true;

    try {
      // Disconnect first if connected
      if (this._isConnected) {
        await this.disconnect();
      }

      // Wait a short moment before reconnecting
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Reconnect
      await this.connect(this.lastConnectionConfig);
      logger.info('AvatarPlayer', 'Reconnection successful');

      trackEvent('rtc_reconnect_success', 'info', {
        provider: this.providerName,
        reconnect_count: this.reconnectCount,
        duration: Math.round(performance.now() - reconnectStart),
      });
    } catch (error) {
      logger.error('AvatarPlayer', 'Reconnection failed:', error);
      trackEvent('rtc_reconnect_failed', 'error', {
        provider: this.providerName,
        reconnect_count: this.reconnectCount,
        description: error instanceof Error ? error.message : String(error),
      });
      // The reconnect failed, so the session really is over. Report it now with
      // the counters the continuation flag preserved. This gets its own reason:
      // a session that died because recovery failed is a different signal from
      // one the server simply dropped.
      this.isSessionContinuation = false;
      this.endSession('reconnect_failed');
      throw error;
    } finally {
      this.isSessionContinuation = false;
      this.isReconnecting = false;
    }
  }

  /**
   * Create internal renderer adapter that bridges AnimationHandler to AvatarView.
   * @internal
   */
  private createRendererAdapter() {
    const avatarView = this.avatarView;

    return {
      renderFromProtobuf(protobufData: ArrayBuffer): void {
        avatarView.renderFromProtobuf(protobufData);
      },

      renderFrame(frame: unknown, startIdle?: boolean): void {
        const view = avatarView as any;
        if (startIdle) {
          view.renderFlame(undefined, true);
        } else if (frame) {
          view.renderFlame(frame);
        }
      },

      async generateTransitionToFrame(
        protobufData: ArrayBuffer,
        frameCount: number,
      ): Promise<unknown[]> {
        return avatarView.generateTransitionToFrame(protobufData, frameCount);
      },

      async generateTransitionToIdle(frameCount: number): Promise<unknown[]> {
        return avatarView.generateTransitionToIdle(frameCount);
      },

      isReady(): boolean {
        return true;
      },
    };
  }

  /**
   * Setup animation track callbacks.
   * @internal
   */
  private async setupAnimationCallbacks(): Promise<void> {
    const callbacks: AnimationTrackCallbacks = {
      onAnimationData: (protobufData, metadata) => {
        // Reset tracking only once per session to avoid duplicate start-packet resets.
        if (!this.hasActiveAnimationSession) {
          if (!this.isTrackingPrimed) {
            this.animationHandler.resetTracking();
          }

          this.hasActiveAnimationSession = true;
          this.isTrackingPrimed = false;

          if (!metadata.isStart) {
            logger.info(
              'AvatarPlayer',
              `Session start inferred from frame seq=${metadata.frameSeq ?? 'n/a'}`,
            );
          }
        } else if (metadata.isStart) {
          logger.info(
            'AvatarPlayer',
            `Ignoring duplicate session start frame seq=${metadata.frameSeq ?? 'n/a'}`,
          );
        }

        this.animationHandler.handleAnimationData(
          protobufData,
          metadata.frameSeq,
          metadata.isRecovered,
        );
      },
      onTransition: (protobufData, transitionFrameCount) => {
        this.conversationCount++;
        void this.sampleTransportStats();
        this.animationHandler.handleTransitionData(
          protobufData,
          transitionFrameCount,
        );
      },
      onTransitionEnd: (protobufData, transitionFrameCount) => {
        void this.sampleTransportStats();
        this.animationHandler.handleTransitionToIdle(
          protobufData,
          transitionFrameCount,
        );
      },
      onSessionStart: () => {
        // Session started - can be used for UI feedback
      },
      onSessionEnd: () => {
        // Session ended - can be used for UI feedback
      },
      onIdleStart: () => {
        // Idle packets can race with start-transition packets; avoid rendering idle mid-transition.
        if (this.animationHandler.isInTransition()) {
          // Keep session bookkeeping in sync even if visual idle is deferred.
          this.hasActiveAnimationSession = false;
          this.isTrackingPrimed = false;
          logger.info(
            'AvatarPlayer',
            'Deferring idleStart while transition is active',
          );
          return;
        }

        this.hasActiveAnimationSession = false;
        this.animationHandler.resetTracking();
        this.isTrackingPrimed = true;
        this.animationHandler.startIdle();
      },
      onStreamStats: (stats) => {
        this.recordStreamStats(stats);
      },
    };

    await this.provider.subscribeAnimationTrack(callbacks);
  }

  /**
   * Setup provider event handlers.
   * @internal
   */
  private setupProviderEvents(): void {
    this.provider.on('connected', () => {
      this._isConnected = true;
    });

    this.provider.on('disconnected', () => {
      const wasConnected = this._isConnected;
      const hostInitiated = this.isTearingDown;
      this._isConnected = false;
      this.hasActiveAnimationSession = false;
      this.isTrackingPrimed = false;
      // Return to idle animation when disconnected
      this.animationHandler.startIdle();

      // Unconditionally, before the endSession() call below: that one is
      // suppressed mid-reconnect and when a summary was already sent, either
      // of which would leave a baseline behind that belongs to a transport
      // that is gone — the next round would difference against it and report
      // the new transport's totals as one round's worth. beginSession() takes
      // a fresh one if the session resumes.
      this.stopTransportStatsSampling();

      // A drop the host never asked for previously left no trace at all: only
      // the flag flipped. Report it here, but leave host-initiated teardowns to
      // disconnect()/detach() — they own the more specific end_reason, and
      // providers fire this event from inside their own disconnect().
      if (wasConnected && !hostInitiated) {
        this.endSession('server_disconnect');
      }
    });

    this.provider.on('error', (error: unknown) => {
      trackEvent('rtc_error', 'error', {
        provider: this.providerName,
        description: error instanceof Error ? error.message : String(error),
      });
    });
  }

  /**
   * Reset per-session provider stream stats.
   * @internal
   */
  private resetStreamStatsAccumulator(): void {
    this.streamStatsAccumulator = {
      samples: 0,
      fpsSum: 0,
      fpsMax: 0,
      framesLost: 0,
      framesRecovered: 0,
      framesDropped: 0,
      framesOutOfOrder: 0,
      framesDuplicate: 0,
    };
    this.previousStreamStatsCounters = null;
  }

  /**
   * Take the transport's opening reading, so the first round has something to
   * measure against.
   *
   * Sampling happens at the two ends of a round (see the transition callbacks),
   * not on a timer: the link only matters while frames are playing, and polling
   * it every couple of seconds meant an idle connection reported for as long as
   * it stayed open — a session left untouched still posted a batch every ten
   * seconds, all of it describing nothing being played.
   *
   * The cumulative counters need a predecessor to be differenced against, and
   * the round-start reading is itself the first half of a round's measurement,
   * so the baseline is taken once here when the transport comes up. It carries
   * across rounds: the counters run for the life of the connection, so an idle
   * stretch between rounds leaves no gap in them.
   *
   * Separate from the stream-stats path below, which is driven by the LiveKit
   * worker and so has no Agora equivalent. This one works for any provider
   * implementing getTransportStats().
   * @internal
   */
  private startTransportStatsSampling(): void {
    if (typeof this.provider.getTransportStats !== 'function') return;

    this.previousTransportCounters = null;
    void this.sampleTransportStats();
  }

  /** @internal */
  private stopTransportStatsSampling(): void {
    this.previousTransportCounters = null;
  }

  /**
   * Read one transport sample and report what moved since the last one.
   *
   * Cumulative counters are differenced; instantaneous measures (RTT, jitter,
   * loss rate) are reported as read. A field the provider does not supply is
   * left out entirely rather than sent as zero — zero is a meaningful value
   * for all of these, and inventing it would read as a flawless link.
   * @internal
   */
  private async sampleTransportStats(): Promise<void> {
    const read = await this.provider.getTransportStats?.();
    if (!read) return;

    const labels = { provider: this.providerName };
    const previous = this.previousTransportCounters;
    this.previousTransportCounters = {
      packetsLost: read.packetsLost,
      packetsReceived: read.packetsReceived,
    };

    // The first sample of a connection is a baseline, not a delta: its
    // counters are totals from before we started watching, and reporting them
    // as one window's worth would post a burst of loss that never happened.
    if (previous) {
      // Same reset handling as the stream counters: a renegotiated transport
      // restarts from zero, and treating that as a large negative delta would
      // be worse than treating the new value as the delta.
      const lostDelta =
        previous.packetsLost !== undefined && read.packetsLost !== undefined
          ? this.getNonNegativeCounterDelta(
              read.packetsLost,
              previous.packetsLost,
            )
          : undefined;
      const recvDelta =
        previous.packetsReceived !== undefined &&
        read.packetsReceived !== undefined
          ? this.getNonNegativeCounterDelta(
              read.packetsReceived,
              previous.packetsReceived,
            )
          : undefined;

      if (lostDelta !== undefined) {
        recordMetric('rtc_transport_rtp_packets_lost', lostDelta, labels);
      }
      // Loss over this window specifically. Providers that report their own
      // rate are preferred (below); this is the fallback for those exposing
      // only counters, and unlike a lifetime ratio it still shows a burst.
      if (
        lostDelta !== undefined &&
        recvDelta !== undefined &&
        read.lossRatePct === undefined
      ) {
        const total = lostDelta + recvDelta;
        if (total > 0) {
          recordMetric(
            'rtc_transport_rtp_loss_rate_pct',
            Math.round((lostDelta / total) * 100),
            labels,
          );
        }
      }
    }

    if (read.lossRatePct !== undefined) {
      recordMetric(
        'rtc_transport_rtp_loss_rate_pct',
        Math.round(read.lossRatePct),
        labels,
      );
    }
    if (read.rttMs !== undefined) {
      recordMetric('rtc_transport_rtt_ms', Math.round(read.rttMs), labels);
    }
    if (read.jitterMs !== undefined) {
      recordMetric(
        'rtc_transport_jitter_ms',
        Math.round(read.jitterMs),
        labels,
      );
    }
  }

  /**
   * Track and aggregate provider stream stats for session telemetry.
   * @internal
   */
  private recordStreamStats(stats: StreamStats): void {
    const deltas = this.getStreamStatsCounterDeltas(stats);

    this.streamStatsAccumulator.samples++;
    this.streamStatsAccumulator.fpsSum += stats.framesPerSec;
    this.streamStatsAccumulator.fpsMax = Math.max(
      this.streamStatsAccumulator.fpsMax,
      stats.framesPerSec,
    );
    this.streamStatsAccumulator.framesLost += deltas.framesLost;
    this.streamStatsAccumulator.framesRecovered += deltas.framesRecovered;
    this.streamStatsAccumulator.framesDropped += deltas.framesDropped;
    this.streamStatsAccumulator.framesOutOfOrder += deltas.framesOutOfOrder;
    this.streamStatsAccumulator.framesDuplicate += deltas.framesDuplicate;

    // Duplicates are deliberately excluded: the sender transmits each packet
    // twice, so a steady trickle of them is the protocol working as designed,
    // not an anomaly. Treating them as one fired this event on nearly every
    // sampling tick. The count still travels on the record for anyone who
    // wants it.
    const isAnomalous =
      deltas.framesLost > 0 ||
      deltas.framesRecovered > 0 ||
      deltas.framesDropped > 0 ||
      deltas.framesOutOfOrder > 0;

    if (!isAnomalous) return;

    // Every count here is this sampling window's delta, not a running total —
    // the two were previously mixed in one record, which made the numbers easy
    // to misread. Names say packets because that is what the transport counts:
    // a packet goes out every 20ms while a new animation frame only arrives
    // every 40ms, so these run at roughly twice the frame rate.
    trackEvent('rtc_stream_stats_anomaly', 'warning', {
      provider: this.providerName,
      packets_per_sec: stats.framesPerSec,
      packets_lost: deltas.framesLost,
      packets_recovered: deltas.framesRecovered,
      packets_dropped: deltas.framesDropped,
      packets_out_of_order: deltas.framesOutOfOrder,
      packets_duplicate: deltas.framesDuplicate,
      // The one running value kept: it identifies *where* in the stream the
      // trouble happened, which the deltas cannot say.
      last_rendered_seq: stats.lastRenderedSeq,
    });

    // Same numbers on the metrics channel, where rate-over-time is what these
    // are actually useful for. The log record above stays as the searchable
    // per-incident detail; labels here are kept low cardinality on purpose.
    //
    // Named transport_* rather than stream_*: these count what the network
    // delivered, whereas the rtc_playback_* series counts what reached the
    // screen. A packet lost in transit but rebuilt from ALR redundancy shows up
    // here and not there, so the two series are a deliberate pairing — the gap
    // between them is the redundancy doing its job. The prefix keeps that
    // distinction visible; names differing by a single word invited reading the
    // two as the same measurement.
    const labels = { provider: this.providerName };
    recordMetric('rtc_transport_packets_lost', deltas.framesLost, labels);
    recordMetric(
      'rtc_transport_packets_recovered',
      deltas.framesRecovered,
      labels,
    );
    recordMetric('rtc_transport_packets_dropped', deltas.framesDropped, labels);
    recordMetric(
      'rtc_transport_packets_out_of_order',
      deltas.framesOutOfOrder,
      labels,
    );
  }

  /**
   * Convert cumulative provider stream counters into per-sample deltas.
   * @internal
   */
  private getStreamStatsCounterDeltas(
    stats: StreamStats,
  ): StreamStatsCounters {
    const current = {
      framesLost: stats.framesLost,
      framesRecovered: stats.framesRecovered,
      framesDropped: stats.framesDropped,
      framesOutOfOrder: stats.framesOutOfOrder,
      framesDuplicate: stats.framesDuplicate,
    };

    const previous = this.previousStreamStatsCounters;
    this.previousStreamStatsCounters = current;

    if (!previous) {
      return current;
    }

    return {
      framesLost: this.getNonNegativeCounterDelta(
        current.framesLost,
        previous.framesLost,
      ),
      framesRecovered: this.getNonNegativeCounterDelta(
        current.framesRecovered,
        previous.framesRecovered,
      ),
      framesDropped: this.getNonNegativeCounterDelta(
        current.framesDropped,
        previous.framesDropped,
      ),
      framesOutOfOrder: this.getNonNegativeCounterDelta(
        current.framesOutOfOrder,
        previous.framesOutOfOrder,
      ),
      framesDuplicate: this.getNonNegativeCounterDelta(
        current.framesDuplicate,
        previous.framesDuplicate,
      ),
    };
  }

  /**
   * Treat counter decreases as provider-side counter resets.
   * @internal
   */
  private getNonNegativeCounterDelta(current: number, previous: number): number {
    return current >= previous ? current - previous : current;
  }

  /**
   * Average transport packet rate for the current session, rounded.
   *
   * Packets, not frames — see the note in endSession(). Must stay an integer:
   * OTLP encodes a non-integer as `{"doubleValue": n}` — a map — but the
   * backend has this field typed as f64 and deserializes it directly, so a
   * fractional value fails the whole request with
   * `invalid type: map, expected f64`, rejecting the entire batch and every
   * record beside it. One tenth of a packet per second is not worth that.
   * @internal
   */
  private getStreamAveragePacketRate(): number {
    if (this.streamStatsAccumulator.samples === 0) {
      return 0;
    }

    return Math.round(
      this.streamStatsAccumulator.fpsSum / this.streamStatsAccumulator.samples,
    );
  }
}
